"use client"

import React, { useEffect } from 'react';
import { supabase } from "@/lib/supabaseClient";
import ThreeScene from '../../ThreeScene.jsx';
import EventEmitter from '../../EventEmitter';
import { sanity, urlFor } from "@/lib/sanityClient";
import { parsePrice } from "@/lib/parsePrice";

/* Helpers */
import { getSessionAndMemberStatus } from '@/lib/authz.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";

/* UI */
import SavingOverlay from "@/components/SavingOverlay";
import ThreeSceneControls from "@/components/ThreeSceneControls";
import { Button } from "@/components/ui/button";
import { Input } from '@/components/ui/input';
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider"
import BookingModal from "@/components/BookingModal";
import { PhoneInput } from "@/components/ui/phone-input";
import { CheckCircle, CircleCheck, Info, RotateCcw, Shapes, ShoppingBag, Sparkles, SparklesIcon, SquarePen } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SelectGroup } from '@radix-ui/react-select';

const STORE_PRODUCT_QUERY = `*[_type=="storeProducts" && productVersionSlug==$productVersionSlug][0]{
  title,
  "slug": slug.current,
  priceLabel,
  about,
  productVersionSlug,
  heroImage{asset->{_id,url}, alt},
  gallery[]{..., "alt": alt}
}`;


// Define the allowed options
const genderOptions = ["Male", "Female", "Other"];

const gen_choices = ["Male", "Female", "Non-binary", "Other"];
const eye_choices = ["Brown", "Blue", "Hazel", "Amber", "Grey", "Green", "Heterochromatic"];
const music_choices = ["Acoustic", "Afro", "Blues", "Breakbeat", "Gospel", "Classical", "Country", "Dance / electronic", "Disco", "Drum & bass", "Folk", "Funk", "Hiphop", "Indie", "Jazz", "K-pop", "Latin", "Metal", "Pop", "Punk", "Reggae", "Rnb", "Rock", "Salsa", "Soul", "Techno", "Traditional", "Other"];
const time_choices = ["Being social with friends / family", "Creative hobby / practice", "Cultural activities", "Gaming", "Reading", "Sleeping", "Sports / exercise", "Watching series / television", "I don't have any free time. I just work", "Other"];
const intro_extro_choices = ["Introvert", "Lightly introvert", "Ambivert", "Lightly extrovert", "Extrovert"];
const reck_caut_choices = ["Highly reckless", "Quite reckless", "Bit of both", "Quite cautious", "Extremely cautious"];
const thisyear_choices = ["Extremely poor", "Bad", "Decent", "Good", "Excellent"];
const commute_choices = ["Bike", "Car", "Public transport", "Walk", "Motorcycle", "Motor scooter", "Uber / taxi", "Mobility aid", "Skate", "Other"];
const time_travel_choices = ["To the future", "To the past", "Stay here"];

// Create a Zod enum schema
const genderSchema = z.enum(gen_choices);
const eyeSchema = z.enum(eye_choices);
const musicSchema = z.enum(music_choices);
const timeSchema = z.enum(time_choices);
const introSchema = z.enum(intro_extro_choices);
const reckSchema = z.enum(reck_caut_choices);
const thisyearSchema = z.enum(thisyear_choices);
const commuteSchema = z.enum(commute_choices);
const time_travelSchema = z.enum(time_travel_choices);

/* Custom Resolver */
const getSchema = (step, mode) => {
  switch (step) {
    case 0:
      // members don't need email, so don't validate it
      if (mode === "member") return z.object({});
      return z.object({
        email: z.string().trim().email("Invalid email address"),
      });
    case 1:
      return z.object({
        first_name: z.string().min(1, 'First name is required'),
        last_name: z.string().min(1, 'Last name is required'),
      });
    case 2:
      return z.object({
        age: z.preprocess((val) => {
          if (typeof val === 'string' || typeof val === 'number') {
            const parsed = Number(val);
            return isNaN(parsed) ? val : parsed;
          }
          return val;
        }, z.number({
          required_error: 'Age is required',
          invalid_type_error: 'Age must be a number',
        }).min(1, 'Age must be at least 1').max(120, 'Age must be less than 120')),
      });
    case 3:
      return z.object({
        gender: z.enum(gen_choices, {
          required_error: 'Gender is required',
          invalid_type_error: 'Invalid gender selection',
        }),
        gender_selfdescribe: z.string().optional(),
      });
    case 4:
      return z.object({
        eye_colour: z.enum(eye_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid gender selection',
        }),
      });
    case 5:
      return z.object({
        music: z.enum(music_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        }),
        music_selfdescribe: z.string().optional(),
      });
    case 6:
      return z.object({
        living: z.string().min(1, '????'),
      });
    case 7:
      return z.object({
        time: z.enum(time_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        }),
        time_selfdescribe: z.string().optional(),
      });
    case 8:
      return z.object({
        compliment: z.string().min(1, '????'),
      });
    case 9:
      return z.object({
        intro_extro: z.enum(intro_extro_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        })
      });
    case 10:
      return z.object({
        reck_caut: z.enum(reck_caut_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        })
      });
    case 11:
      return z.object({
        superpower: z.string().min(1, '????'),
      });
    case 12:
      return z.object({
        personality: z.string().min(1, '????'),
      });
    case 13:
      return z.object({
        thisyear: z.enum(thisyear_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        })
      });
    case 14:
      return z.object({
        commute: z.enum(commute_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid selection',
        }),
        commute_selfdescribe: z.string().optional(),
      });
    case 15:
      return z.object({
        fear: z.string().min(1, '????'),
      });
    case 16:
      return z.object({
        memory: z.string().min(1, '????'),
      });
    case 17:
      return z.object({
        time_travel: z.enum(time_travel_choices, {
          required_error: 'Choice is required',
          invalid_type_error: 'Invalid gender selection',
        }),
      });
    case 18:
      return z.object({
        proud: z.string().min(1, '????'),
      });
    default:
      return z.object({});
  }
};

const MyForm = () => {
/* Flow */
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const introSlide = 0
  const amountOfQuestions = 18;
  const reserveSlide = amountOfQuestions + 1 // 19
  const thankYouSlide = amountOfQuestions + 2 // 20
  const checkoutSlide = amountOfQuestions + 3 // 21
  const playgroundSlide = amountOfQuestions + 4; // 22

  const [creationId, setCreationId] = React.useState(null);
  const [authUserId, setAuthUserId] = React.useState(null);
  const [reservationId, setReservationId] = React.useState(null);
  const [mode, setMode] = React.useState(null); // null = loading
  const [pv, setPv] = React.useState(null); // product version row
  const isPlayground = pv?.channel === "playground";
  const pvLoaded = pv !== null; // or pv?.id

  const [sessionEmail, setSessionEmail] = React.useState(null);
  const persistEnabledRef = React.useRef(true);
  const stepRef = React.useRef(step);

  const [storeProduct, setStoreProduct] = React.useState(null);

  useEffect(() => {
    let alive = true;
  
    (async () => {
      try {
        const res = await sanity.fetch(STORE_PRODUCT_QUERY, {
          productVersionSlug: PRODUCT_VERSION_SLUG,
        });
  
        if (!alive) return;
        setStoreProduct(res);
      } catch (err) {
        console.error("Failed to load store product from Sanity:", err);
      }
    })();
  
    return () => {
      alive = false;
    };
  }, []);

  React.useEffect(() => {
    stepRef.current = step;
  }, [step]);

   /* Load item */
   const PRODUCT_VERSION_SLUG = "labyrinth-scarf-v2";

   useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("product_versions")
        .select("id, visibility, channel, config, slug")
        .eq("slug", PRODUCT_VERSION_SLUG)
        .single();
  
      if (!alive) return;
      if (error) {
        console.error("Failed to load product_version:", error);
        return;
      }
      setPv(data);
    })();
  
    return () => { alive = false; };
  }, []);
  
  /* Load user */
  useEffect(() => {
    let alive = true;
    (async () => {
      const { session, isAnon, isMember } = await getSessionAndMemberStatus();
      if (!alive) return;

      if (session?.user && !isAnon && isMember) {
        setMode("member");
        setAuthUserId(session.user.id);
        setSessionEmail(session.user.email ?? null);
      } else {
        setMode("public");
        // Public flow: sign in anonymously early so you can save drafts under RLS
        const u = await ensureAnonSession();
        setAuthUserId(u.id);
      }
    })();
    return () => { alive = false; };
  }, []);

  const CREATION_KEY = "ls_labyrinth_creation_id";

  /* Local storage & recovery */
  React.useEffect(() => {
    const saved = localStorage.getItem(CREATION_KEY);
    if (saved && !creationId) setCreationId(saved);

    // optional: re-hydrate anon/member auth quickly
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user && !authUserId) setAuthUserId(data.user.id);
    })();
  }, [creationId, authUserId]);

  const LS_KEYS = [
    CREATION_KEY,
    "ls_labyrinth_answers",
    "ls_labyrinth_step",
    "ls_labyrinth_updated_at",
  ];

  const [hasLocalDraft, setHasLocalDraft] = React.useState(false);

  function detectLocalDraft() {
    const has =
      !!localStorage.getItem("ls_labyrinth_answers") ||
      !!localStorage.getItem("ls_labyrinth_step") ||
      !!localStorage.getItem(CREATION_KEY);

    setHasLocalDraft(has);
    return has;
  }

  async function startOver() {
    // Stop the watcher from writing anything during reset
    persistEnabledRef.current = false;

    LS_KEYS.forEach((k) => localStorage.removeItem(k));

    setCreationId(null);
    setReservationId(null);

    form.reset(EMPTY_VALUES);
    form.clearErrors();

    setStep(introSlide);
    setHasLocalDraft(false);

    // Force remount clean
    setFormKey((k) => k + 1);

    // Re-enable persistence on next tick (after state settles)
    setTimeout(() => {
      persistEnabledRef.current = true;
    }, 0);
  }

  useEffect(() => {
    persistEnabledRef.current = false;

    const raw = localStorage.getItem("ls_labyrinth_answers");
    const savedStep = localStorage.getItem("ls_labyrinth_step");

    if (raw) {
      try {
        const values = JSON.parse(raw);
        form.reset({ ...form.getValues(), ...values });
      } catch { }
    }

    if (savedStep) {
      const s = Number(savedStep);
      if (!Number.isNaN(s)) setStep(s);
    }

    detectLocalDraft();

    setTimeout(() => {
      persistEnabledRef.current = true;
    }, 0);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sceneRef = React.useRef(null);
  const [isSaving, setIsSaving] = React.useState(false);

  /* Form values */
  // Initialize useForm with dynamic resolver based on step
  const form = useForm({
    resolver: zodResolver(getSchema(step, mode ?? "public")),
    defaultValues: {

      email: "",
      phone_e164: "",
      first_name: "",
      last_name: "",
      age: "",
      gender: "",
      gender_selfdescribe: "",
      eye_colour: "",
      music: "",
      music_selfdescribe: "",
      living: "",
      time: "",
      time_selfdescribe: "",
      compliment: "",
      intro_extro: "",
      reck_caut: "",
      superpower: "",
      personality: "",
      thisyear: "",
      commute: "",
      commute_selfdescribe: "",
      fear: "",
      memory: "",
      time_travel: "",
      proud: "",

    },
  });

  const EMPTY_VALUES = {

    email: "",
    phone_e164: "",
    first_name: "",
    last_name: "",
    age: "",
    gender: "",
    gender_selfdescribe: "",
    eye_colour: "",
    music: "",
    music_selfdescribe: "",
    living: "",
    time: "",
    time_selfdescribe: "",
    compliment: "",
    intro_extro: "",
    reck_caut: "",
    superpower: "",
    personality: "",
    thisyear: "",
    commute: "",
    commute_selfdescribe: "",
    fear: "",
    memory: "",
    time_travel: "",
    proud: "",
  };

  const [formKey, setFormKey] = React.useState(0);

  /* Default for Sliders */
  useEffect(() => {
    if (step === 9) {
      const current = form.getValues("intro_extro");
      if (!current) {
        form.setValue("intro_extro", intro_extro_choices[2], {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }

    if (step === 10) {
      const current = form.getValues("reck_caut");
      if (!current) {
        form.setValue("reck_caut", reck_caut_choices[2], {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }

    if (step === 13) {
      const current = form.getValues("thisyear");
      if (!current) {
        form.setValue("thisyear", thisyear_choices[2], {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [step, form]);

  /* Sent to P5JS */
  // Watch form values
  const first_name = form.watch('first_name');
  const last_name = form.watch('last_name');
  const age = form.watch('age');
  const gender = form.watch('gender');
  const gender_selfdescribe = form.watch('gender_selfdescribe');
  const eye_colour = form.watch('eye_colour');
  const music = form.watch('music');
  const music_selfdescribe = form.watch('music_selfdescribe');
  const living = form.watch('living');
  const time = form.watch('time');
  const time_selfdescribe = form.watch('time_selfdescribe');
  const compliment = form.watch('compliment');
  const intro_extro = form.watch('intro_extro');
  const reck_caut = form.watch('reck_caut');
  const superpower = form.watch('superpower');
  const personality = form.watch('personality');
  const thisyear = form.watch('thisyear');
  const commute = form.watch('commute');
  const commute_selfdescribe = form.watch('commute_selfdescribe');
  const fear = form.watch('fear');
  const memory = form.watch('memory');
  const time_travel = form.watch('time_travel');
  const proud = form.watch('proud');

  // Default for Selfdescribe
  useEffect(() => {
    if (gender !== "Other") {
      form.setValue('gender_selfdescribe', '');
    }
  }, [gender, form]);

  useEffect(() => {
    if (music !== "Other") {
      form.setValue('music_selfdescribe', '');
    }
  }, [music, form]);

  useEffect(() => {
    if (time !== "Other") {
      form.setValue('time_selfdescribe', '');
    }

  }, [time, form]);
  useEffect(() => {
    if (commute !== "Other") {
      form.setValue('commute_selfdescribe', '');
    }
  }, [commute, form]);

  // Use useEffect to dispatch updates when form values change
  React.useEffect(() => {
    EventEmitter.dispatch('updateData', {
      first_name, last_name, age,
      gender, gender_selfdescribe,
      eye_colour, music,
      music_selfdescribe, living,
      time, time_selfdescribe, compliment,
      intro_extro, reck_caut, superpower,
      personality, thisyear, commute,
      commute_selfdescribe, fear, memory,
      time_travel, proud,
    });
  }, [first_name, last_name, age,
    gender, gender_selfdescribe,
    eye_colour, music,
    music_selfdescribe, living,
    time, time_selfdescribe, compliment,
    intro_extro, reck_caut, superpower,
    personality, thisyear, commute,
    commute_selfdescribe, fear, memory,
    time_travel, proud,]);

  /* Auth helper */
  async function ensureAnonSession() {
    const { data } = await supabase.auth.getUser();
    if (data?.user) return data.user;

    const { data: signed, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    return signed.user;
  }

  /* Local storage */
  // Save draft on every change
  useEffect(() => {
    const sub = form.watch((values) => {
      if (!persistEnabledRef.current) return;

      localStorage.setItem("ls_labyrinth_answers", JSON.stringify(values));
      localStorage.setItem("ls_labyrinth_step", String(stepRef.current));
      localStorage.setItem("ls_labyrinth_updated_at", new Date().toISOString());
    });

    return () => sub.unsubscribe();
  }, [form]);

  /* Load item */
  async function getProductVersionIdBySlug(slug) {
    const { data, error } = await supabase
      .from("product_versions")
      .select("id, visibility, channel, config")
      .eq("slug", slug)
      .single();

    if (error) throw error;
    return data.id;
  }

  /* CreationID && Auth */
  async function ensureCreation() {
    // 1) reuse existing id if we already have one in state/localStorage
    const cached = creationId ?? localStorage.getItem(CREATION_KEY);
    if (cached) {
      setCreationId(cached);
      return cached;
    }

    // 2) ensure we have an auth user id (member uid OR anon uid)
    let uid = authUserId;
    if (!uid) {
      if (mode === "member") {
        const { data } = await supabase.auth.getUser();
        uid = data?.user?.id ?? null;
        if (!uid) throw new Error("No signed-in member session found.");
        setAuthUserId(uid);
      } else {
        const anon = await ensureAnonSession();
        uid = anon.id;
        setAuthUserId(uid);
      }
    }

    // 3) product version
    const product_version_id = await getProductVersionIdBySlug(PRODUCT_VERSION_SLUG);

    // 4) PUBLIC ONLY: create canonical public user by email
    let public_user_id = null;
    if (mode === "public") {
      const email = form.getValues("email")?.trim().toLowerCase();
      if (!email) {
        // If user is public, they must enter email before Start.
        // We'll rely on step 0 validation too.
        throw new Error("Email missing");
      }

      const { data: publicUserId, error: rpcErr } = await supabase.rpc(
        "get_or_create_public_user",
        {
          p_email: email,
          p_first_name: form.getValues("first_name")?.trim() || "",
          p_last_name: form.getValues("last_name")?.trim() || "",
          p_source: "public_labyrinth_scarf_trial",
        }
      );
      if (rpcErr) throw rpcErr;
      public_user_id = publicUserId;
    }

    // 5) create creation row (draft)
    const { data: created, error: cErr } = await supabase
      .from("creations")
      .insert([
        {
          auth_user_id: uid,
          public_user_id, // null for members
          product_version_id,
          status: "draft",
          source: mode === "member" ? "member_create" : "public_labyrinth_scarf_trial",
        },
      ])
      .select("id")
      .single();

    if (cErr) throw cErr;

    setCreationId(created.id);
    localStorage.setItem(CREATION_KEY, created.id);
    return created.id;
  }

  /* Navigation */
  const handleBack = () => setStep((prevStep) => prevStep - 1);
  const handleNext = () => setStep((prevStep) => prevStep + 1);

  const onSubmit = async (data) => {
    console.log("Submitted Data:", data);

    // Always make sure we have a creation id (created at Start / first Next)
    const cid = creationId ?? (await ensureCreation());

    // Merge current step values with full form state
    const allData = { ...form.getValues(), ...data };

    // Not final step → just go next
    if (step !== amountOfQuestions) {
      handleNext();
      return;
    }

    // -------- FINAL SUBMIT --------
    const {
      first_name,
      last_name,
      age,
      gender,
      gender_selfdescribe,
      eye_colour,
      music,
      music_selfdescribe,
      living,
      time,
      time_selfdescribe,
      compliment,
      intro_extro,
      reck_caut,
      superpower,
      personality,
      thisyear,
      commute,
      commute_selfdescribe,
      fear,
      memory,
      time_travel,
      proud,
    } = allData;

    try {
      // If you want: mark creation as "completed" (or keep draft until reserve)
      // await supabase.from("creations").update({ status: "completed" }).eq("id", cid);

      // For PUBLIC: ensure canonical user exists and attach to creation
      // For MEMBER: skip public user entirely
      if (mode === "public") {
        const email = form.getValues("email")?.trim().toLowerCase();
        if (!email) {
          form.setError("email", { type: "manual", message: "Email required" });
          return;
        }

        const { data: publicUserId, error: rpcErr } = await supabase.rpc(
          "get_or_create_public_user",
          {
            p_email: email,
            p_first_name: (first_name ?? "").trim(),
            p_last_name: (last_name ?? "").trim(),
            p_source: "public_labyrinth_scarf_trial",
          }
        );
        if (rpcErr) throw rpcErr;

        // attach canonical public user to the *existing* creation
        const { error: linkErr } = await supabase
          .from("creations")
          .update({
            public_user_id: publicUserId,
            source: "public_labyrinth_scarf_trial",
          })
          .eq("id", cid);

        if (linkErr) throw linkErr;
      }

      // Upsert (or insert) answers for THIS creation (no new creation created!)
      // If you want multiple attempts per creation, use insert instead of upsert.
      const { data: lsRow, error: lsErr } = await supabase
        .from("Labyrinth Scarf")
        .upsert(
          [
            {
              creation_id: cid,
              first_name: (first_name ?? "").trim(),
              last_name: (last_name ?? "").trim(),
              age,
              gender,
              gender_selfdescribe,
              eye_colour,
              music,
              music_selfdescribe,
              living,
              time,
              time_selfdescribe,
              compliment,
              intro_extro,
              reck_caut,
              superpower,
              personality,
              thisyear,
              commute,
              commute_selfdescribe,
              fear,
              memory,
              time_travel,
              proud,
              source: mode === "member" ? "member_create" : "public_labyrinth_scarf_trial",
              status: "new",
            },
          ],
          { onConflict: "creation_id" } // ✅ assumes unique index on creation_id in "Labyrinth Scarf"
        )
        .select("id, creation_id")
        .single();

      if (lsErr) throw lsErr;

      // Save images against the same creation id
      try {
        setIsSaving(true);
        await sceneRef.current?.saveAssets(cid);
      } finally {
        setIsSaving(false);
      }

      setReservationId(lsRow?.id ?? null);

      if (isPlayground) {
        setStep(playgroundSlide);
      } else {
        /* setStep(mode === "member" ? checkoutSlide : reserveSlide); */
        setStep(checkoutSlide);
      }

    } catch (err) {
      console.error("Final submit failed:", err);
    }
  };


  const handleReserve = async () => {
    if (!creationId) {
      console.error("Missing creationId");
      return;
    }

    const uid = authUserId ?? (await ensureAnonSession()).id;
    setAuthUserId(uid);

    const email = form.getValues("email")?.trim().toLowerCase();
    const phoneRaw = form.getValues("phone_e164");

    // validate email (optional if you already trigger)
    if (!email) {
      form.setError("email", { type: "manual", message: "Please enter your email" });
      return;
    }

    const parsed = parsePhoneNumberFromString(phoneRaw || "");
    if (!parsed || !parsed.isValid()) {
      form.setError("phone_e164", { type: "manual", message: "Please enter a valid phone number" });
      return;
    }

    const phone_country_iso2 = parsed.country || null;
    const phone_calling_code = `+${parsed.countryCallingCode}`;
    const phone_national = parsed.nationalNumber;
    const phone_e164 = parsed.number;

    // ✅ update canonical user contact (optional but recommended)
    const { error: contactErr } = await supabase
      .rpc("update_public_user_contact_by_email", {
        p_email: email,
        p_phone_country_iso2: phone_country_iso2,
        p_phone_calling_code: phone_calling_code,
        p_phone_national: phone_national,
        p_phone_e164: phone_e164,
      });

    if (contactErr) {
      console.error("update contact failed:", contactErr);
      // do NOT block reservation if you prefer
    }

    // 2) upsert order (one per creation)
    const { error: orderErr } = await supabase
      .from("orders")
      .upsert([{
        creation_id: creationId,
        first_name: form.getValues("first_name")?.trim() || null,
        last_name: form.getValues("last_name")?.trim() || null,
        email,
        phone_e164,
        status: "reserved",
        reserved_at: new Date().toISOString(),
        source: "public_labyrinth_scarf_trial",
      }], { onConflict: "creation_id" });

    if (orderErr) { console.error(orderErr); return; }

    // 3) update creation status -> reserved
    const { error: creationErr } = await supabase
      .from("creations")
      .update({
        status: "reserved",
      })
      .eq("id", creationId);

    if (creationErr) {
      console.error("Failed to update creation status:", creationErr);
      return;
    }

    setStep(thankYouSlide);
  };

  const handleKeyDown = (e) => {
    const invalidChars = ['-', '+', 'e', 'E', '.'];
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  /*   Snipcart */
  const imageUrl = `https://jiyrakguobqprdfihdnb.supabase.co/storage/v1/object/public/creations-public/creations/${creationId}/final_p5.png`

/*   const checkoutPrice = Number(
    String(storeProduct?.priceLabel ?? "").replace(",", ".").replace(/[^\d.]/g, "")
  ); */

  const checkoutPrice = parsePrice(storeProduct?.priceLabel);

// Make it absolute (important for Snipcart validation + emails)
const absoluteImageUrl = new URL(imageUrl, window.location.origin).toString();

const confirmationDescription = `Material: 100% Silk • Packaging: Branded Packaging (standard)`;

  return (
    <div className="flex flex-col md:h-full px-2 mt-2">
  <div className="flex-1 flex flex-col md:flex-row gap-2">

        {/* Recover answers UI */}
        {hasLocalDraft && (
          <div className="absolute right-4 px-2 pr-3 mb-0 text-xs font-sans text-black flex gap-4 items-center justify-start">
            <div className='flex gap-2'><CircleCheck className='h-4 w-4' /> <p>Creation recovered</p></div> <Button
              variant="secondary"
              onClick={startOver}
              className="text-xs font-sans text-black hover:text-black px-4 bg-white/80 hover:bg-white border rounded-full"
            ><div className='flex items-center gap-2'><RotateCcw className='h-4 w-4 text-back' />
                <p>Start over</p></div>
            </Button>
          </div>
        )}

    {/* LEFT: Three */}
    <div
      className="
        relative flex items-center justify-center
        w-full md:w-1/2
        h-[45vh] md:h-[calc(100vh-10.1rem)]
        rounded-3xl overflow-hidden bg-[#ddd]
      "
    >
          <ThreeScene ref={sceneRef} />
          <SavingOverlay show={isSaving} />
          <ThreeSceneControls sceneRef={sceneRef} disabled={isSaving} positionClassName="bottom-3" />

          {/* Explainer UI */}
          <div className="absolute inset-0 pointer-events-none flex items-start justify-center bg-transparent mx-4 mt-4 ">
            <div className="pointer-events-none flex rounded-xl bg-white/70 backdrop-blur border border-white/70 px-4 py-3 text-xs text-center text-black/60 font-sans w-64">
              <SparklesIcon className="text-black h-4" />
              <p>
                Your <span className="text-black p-[2px]">Labyrinth Scarf</span> will be designed below
                based on your answers.
              </p>
            </div>
          </div>
        </div>

{/* RIGHT: Form */}
<div className="w-full md:w-1/2 flex md:items-center justify-center">
      <div className="w-full max-w-prose p-0 mb-24 md:mb-0 md:p-8">


      <div className="flex-1 flex items-center justify-center">
          <div className="flex-1 max-w-prose md:p-8">
          {!pvLoaded ? ( 
            <div className="p-8 rounded-2xl border-2 border-white bg-white/60 backdrop-blur">
            <p className="text-sm font-sans text-black/60">Loading…</p>
          </div>
           ):(
            <div className='rounded-3xl border-2 border-white bg-white/60 backdrop-blur p-8 py-16 md:p-8'>

              {step === introSlide && (

                <Form key={formKey} {...form}>
                  <form
                    onSubmit={form.handleSubmit(async () => {
                      await ensureCreation(); // create draft row now
                      setStep(1);
                    })}
                  >
                    <p className="flex items-center text-sm mb-1 text-black font-sans text-black/60"><Info className=" h-4 w-4 mr-2" />How it works</p>
                    <h1 className="text-3xl mb-4 font-kalice font-medium">
                      Create your <span className="font-kalice text-black">Labyrinth Scarf</span>
                    </h1>

                    <p className="mb-4 font-sans text-sm text-black/60">
                      {isPlayground ? (
                        <>This is a playground prototype to test the experience. Nothing is for sale yet.</>
                      ) : (
                        <>This questionnaire will guide you to create your own ‘Labyrinth Scarf’. The design is a
                        direct translation of the answers you give. When ready, click ‘Submit’ to review and order
                        your design.</>
                      )}
                    </p>

                    {mode === "member" && sessionEmail && (
                      <div className=" w-fit my-6 p-2 pr-3 mb-0 text-xs font-sans text-black/60 flex gap-2 bg-white border rounded-full items-center justify-start">
                        <CircleCheck className='h-4 w-4' /> You're signed in as <span className="text-black">{sessionEmail}</span>
                      </div>
                    )}

                    <div className="space-y-2 my-4">
                      {mode === "public" && (
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-sm font-sans text-black">Email</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="email"
                                  placeholder="Type here..."
                                  className="border-black w-full p-2 border-2 bg-transparent"
                                />
                              </FormControl>
                              <p className="text-xs font-sans text-black/50">
                                We’ll only use this to save and confirm your submission.
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                    <Button
                      variant="secondary"
                      type="submit"
                      className="mt-2 w-full font-sans rounded-none bg-black text-white hover:bg-black/90"
                    >
                      Start
                    </Button>
                  </form>
                </Form>
              )}

              {step > introSlide && step < reserveSlide && (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <p className="text-sm mb-1 text-black">Question {step}/{amountOfQuestions}</p>
                    {step === 1 && (
                      <>
                        <h1 className="text-3xl mb-4 font-kalice font-medium">What is your name?</h1>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                          <FormField
                            control={form.control}
                            name="first_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>First name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Type here..." {...field} className="border-black w-full p-2 border-2 mb-4 bg-transparent" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="last_name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Last name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Type here..." {...field} className="border-black w-full p-2 border-2 mb-4 bg-transparent" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </>

                    )}
                    {step === 2 && (
                      <>
                        <h1 className="text-3xl mb-4 font-kalice font-medium">What is your age?</h1>
                        <FormField
                          control={form.control}
                          name="age"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>

                                <Input className="border-black w-full p-2 border-2 mb-4 bg-transparent"
                                  type="number"
                                  placeholder=""
                                  {...field}
                                  min="1"
                                  max="120"
                                  step="1"
                                  onKeyDown={handleKeyDown}
                                />

                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>

                    )}
                    {step === 3 && (
                      <>
                        <FormField
                          control={form.control}
                          name="gender"
                          render={({ field }) => (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">Which best describes your gender?</h1>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  value={field.value}
                                >
                                  <div
                                    className="my-4">
                                    {gen_choices.map((option) => (
                                      <FormItem
                                        key={option}
                                        className="flex items-center space-x-2"
                                      >
                                        <FormControl>
                                          <RadioGroupItem value={option} id={option} />
                                        </FormControl>
                                        <Label htmlFor={option}>{option}</Label>
                                      </FormItem>
                                    ))}
                                  </div>
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Selfdescribe */}
                        {gender === "Other" && (

                          <div
                            className="my-4">
                            <FormField
                              control={form.control}
                              name="gender_selfdescribe"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Self-describe (optional)</FormLabel>
                                  <FormControl>
                                    <Input className=" w-full p-2 border-2 mb-4 border-black bg-transparent" placeholder="Type here..." {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}

                      </>
                    )}
                    {step === 4 && (

                      <FormField
                        control={form.control}
                        name="eye_colour"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">Which best describes the colour of your eyes?</h1>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <div
                                  className="my-4">
                                  {eye_choices.map((option) => (
                                    <FormItem
                                      key={option}
                                      className="flex items-center space-x-2"
                                    >
                                      <FormControl>
                                        <RadioGroupItem value={option} id={option} />
                                      </FormControl>
                                      <Label htmlFor={option}>{option}</Label>
                                    </FormItem>
                                  ))}
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                    )}
                    {step === 5 && (
                      <>
                        <FormField
                          control={form.control}
                          name="music"
                          render={({ field }) => (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">What music genre do you listen to mostly?</h1>
                              <FormControl>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <SelectTrigger className="border-black border-2">
                                    <SelectValue placeholder="Choose one" />
                                  </SelectTrigger>
                                  <SelectContent className="dark bg-[#111]">
                                    <SelectGroup className=''>
                                      {music_choices.map((option) => (
                                        <SelectItem key={option} value={option} className="data-[highlighted]:bg-[#3a3a3a] data-[highlighted]:text-white">
                                          {option}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Selfdescribe */}
                        {music === "Other" && (

                          <div
                            className="my-4">
                            <FormField
                              control={form.control}
                              name="music_selfdescribe"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Self-describe (optional)</FormLabel>
                                  <FormControl>
                                    <Input className="border-black w-full p-2 border-2 mb-4 bg-transparent" placeholder="Type here..." {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}

                      </>
                    )}
                    {step === 6 && (
                      <FormField
                        control={form.control}
                        name="living"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What do you do for a living?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 7 && (
                      <>
                        <FormField
                          control={form.control}
                          name="time"
                          render={({ field }) => (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">How do you like to spend your free time?</h1>
                              <FormControl>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <SelectTrigger className="border-black border-2">
                                    <SelectValue placeholder="Choose one" />
                                  </SelectTrigger>
                                  <SelectContent className="dark bg-[#111]">
                                    <SelectGroup>
                                      {time_choices.map((option) => (
                                        <SelectItem key={option} value={option} className="data-[highlighted]:bg-[#3a3a3a] data-[highlighted]:text-white"  >
                                          {option}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Selfdescribe */}
                        {time === "Other" && (

                          <div
                            className="my-4">
                            <FormField
                              control={form.control}
                              name="time_selfdescribe"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Self-describe (optional)</FormLabel>
                                  <FormControl>
                                    <Input className="border-black w-full p-2 border-2 mb-4 bg-transparent" placeholder="Type here..." {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}

                      </>
                    )}
                    {step === 8 && (
                      <FormField
                        control={form.control}
                        name="compliment"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What compliment do you hear the most?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 9 && (
                      <FormField
                        control={form.control}
                        name="intro_extro"
                        render={({ field }) => {
                          //const choices = [thisyear_choices[0], ":", thisyear_choices[2], ":", thisyear_choices[4]];
                          const currentIndex = intro_extro_choices.includes(field.value) ? intro_extro_choices.indexOf(field.value) : 2; // Default to "Decent"
                          return (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">Are you more introverted or extroverted?</h1>
                              <FormControl>

                                <div className="relative">
                                  <div
                                    className='my-6'>
                                    <Slider
                                      className="mt-12"
                                      min={0}
                                      max={4}
                                      step={1}
                                      value={[currentIndex]}
                                      onValueChange={(value) => {
                                        const index = value[0];
                                        const selectedChoice = intro_extro_choices[index];
                                        field.onChange(selectedChoice);
                                      }}
                                    />
                                    {/* Labels under the slider */}
                                    <div className="flex  justify-between mt-6">
                                      {intro_extro_choices.map((label, index) => (
                                        <span
                                          key={index}
                                          className="text-md font-sans  text-center"
                                        >
                                          {(index === 0 || index === 2 || index === 4) ? label : '|'}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />


                    )}
                    {step === 10 && (
                      <FormField
                        control={form.control}
                        name="reck_caut"
                        render={({ field }) => {
                          //const choices = [thisyear_choices[0], ":", thisyear_choices[2], ":", thisyear_choices[4]];
                          const currentIndex = reck_caut_choices.includes(field.value) ? reck_caut_choices.indexOf(field.value) : 2; // Default to "Decent"
                          return (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">Are you more reckless or cautious?</h1>
                              <FormControl>

                                <div className="relative">
                                  <div
                                    className='my-6'>
                                    <Slider
                                      className="mt-12"
                                      min={0}
                                      max={4}
                                      step={1}
                                      value={[currentIndex]}
                                      onValueChange={(value) => {
                                        const index = value[0];
                                        const selectedChoice = reck_caut_choices[index];
                                        field.onChange(selectedChoice);
                                      }}
                                    />
                                    {/* Labels under the slider */}
                                    <div className="flex  justify-between mt-6">
                                      {reck_caut_choices.map((label, index) => (
                                        <span
                                          key={index}
                                          className=" text-center"
                                        >
                                          {(index === 0 || index === 2 || index === 4) ? label : '|'}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />


                    )}
                    {step === 11 && (
                      <FormField
                        control={form.control}
                        name="superpower"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What is your superpower?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 12 && (
                      <FormField
                        control={form.control}
                        name="personality"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">How would you describe yourself?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 13 && (
                      <FormField
                        control={form.control}
                        name="thisyear"
                        render={({ field }) => {
                          //const choices = [thisyear_choices[0], ":", thisyear_choices[2], ":", thisyear_choices[4]];
                          const currentIndex = thisyear_choices.includes(field.value) ? thisyear_choices.indexOf(field.value) : 2; // Default to "Decent"
                          return (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">How was your year?</h1>
                              <FormControl>

                                <div className="relative">
                                  <div
                                    className='my-6'>
                                    <Slider
                                      className="mt-12"
                                      min={0}
                                      max={4}
                                      step={1}
                                      value={[currentIndex]}
                                      onValueChange={(value) => {
                                        const index = value[0];
                                        const selectedChoice = thisyear_choices[index];
                                        field.onChange(selectedChoice);
                                      }}
                                    />
                                    {/* Labels under the slider */}
                                    <div className="flex  justify-between mt-6">
                                      {thisyear_choices.map((label, index) => (
                                        <span
                                          key={index}
                                          className=" text-center"
                                        >
                                          {(index === 0 || index === 2 || index === 4) ? label : '|'}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />


                    )}
                    {step === 14 && (
                      <>
                        <FormField
                          control={form.control}
                          name="commute"
                          render={({ field }) => (
                            <FormItem>
                              <h1 className="text-3xl mb-4 font-kalice font-medium">How do you mostly travel around?</h1>
                              <FormControl>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <SelectTrigger className="border-black border-2">
                                    <SelectValue placeholder="Choose one" />
                                  </SelectTrigger>
                                  <SelectContent className="dark bg-[#111]">
                                    <SelectGroup>
                                      {commute_choices.map((option) => (
                                        <SelectItem key={option} value={option} className="data-[highlighted]:bg-[#3a3a3a] data-[highlighted]:text-white">
                                          {option}
                                        </SelectItem>
                                      ))}
                                    </SelectGroup>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* Selfdescribe */}
                        {commute === "Other" && (

                          <div
                            className="my-4">
                            <FormField
                              control={form.control}
                              name="commute_selfdescribe"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Self-describe (optional)</FormLabel>
                                  <FormControl>
                                    <Input className="border-black w-full p-2 border-2 mb-4 bg-transparent" placeholder="Type here..." {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        )}

                      </>
                    )}
                    {step === 15 && (
                      <FormField
                        control={form.control}
                        name="fear"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What is your biggest fear?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 16 && (
                      <FormField
                        control={form.control}
                        name="memory"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What memory will you never forget?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {step === 17 && (

                      <FormField
                        control={form.control}
                        name="time_travel"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">If you could time travel where would you go?</h1>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <div
                                  className="my-4">
                                  {time_travel_choices.map((option) => (
                                    <FormItem
                                      key={option}
                                      className="flex items-center space-x-2"
                                    >
                                      <FormControl>
                                        <RadioGroupItem value={option} id={option} />
                                      </FormControl>
                                      <Label htmlFor={option}>{option}</Label>
                                    </FormItem>
                                  ))}
                                </div>
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                    )}
                    {step === 18 && (
                      <FormField
                        control={form.control}
                        name="proud"
                        render={({ field }) => (
                          <FormItem>
                            <h1 className="text-3xl mb-4 font-kalice font-medium">What are you most proud of?</h1>
                            <FormControl>
                              <Textarea
                                placeholder="Type your answer here..."
                                {...field}
                                className="border-black w-full p-2 border-2 mb-4 resize-none bg-transparent"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    <div className="flex justify-between mt-12 w-full">
                      <Button variant="outline" type="button" className=" px-12 mr-2 border-2 font-sans border-black rounded-none text-black mt-4 hover:bg-black hover:text-white hover:border-black bg-transparent" onClick={handleBack}>Back</Button>
                      <Button variant="secondary" type="submit" className="w-full px-12 mt-4 font-sans rounded-none bg-black text-white hover:bg-black/90">{step === amountOfQuestions ? "Complete" : "Next"}</Button>
                    </div>
                  </form>
                </Form>
              )}
              {/* --- Slide 19: Reserve design --- */}
              {!isPlayground && step === reserveSlide && (
                <Form {...form}>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const ok = await form.trigger(["email", "phone_e164"]);
                      if (!ok) return;
                      handleReserve();
                    }}
                    className="space-y-2"
                  >
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <div className="flex gap-2 text-sm text-black font-sans">
                          <Sparkles className="h-4 w-4 text-black" strokeWidth={1.75} />
                          <span>Design completed</span>
                        </div>

                        <h1 className="text-3xl font-kalice font-medium text-black">
                          Your Labyrinth Scarf is ready.
                        </h1>

                        <p className="text-sm font-sans text-black/60">
                          Reserve your design to keep it on file and receive the next steps forward. Orders start{" "}
                          <span className="text-black">from €{checkoutPrice}</span> and are handmade in The Netherlands.
                        </p>
                      </div>

                      <div className="pb-6">
                        <div className="space-y-4">
                          {/* Email */}
                          <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem className="space-y-2 my-4">
                                <FormLabel className="text-sm font-sans text-black">Email</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    type="email"
                                    placeholder="Type here..."
                                    className="border-black w-full p-2 border-2"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          {/* Phone */}
                          <FormField
                            control={form.control}
                            name="phone_e164"
                            render={({ field }) => (
                              <FormItem className="space-y-2 my-4">
                                <FormLabel className="text-sm font-sans text-black">
                                  Phone (WhatsApp)
                                </FormLabel>
                                <FormControl>
                                  <PhoneInput
                                    defaultCountry="NL"
                                    international
                                    placeholder="Enter phone number"
                                    value={field.value}
                                    onChange={(v) => field.onChange(v ?? "")}
                                  />
                                </FormControl>
                                <FormMessage />
                                <p className="text-xs font-sans text-black/50">
                                  We’ll reach out within 1–2 working days to confirm details and share a personal order link.
                                </p>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="grid grid-cols-2 gap-2 w-full">
                        <Link
                          to="/">
                          <Button
                            variant="outline"
                            type="button"
                            className="border-2 font-sans border-black rounded-none text-black w-full hover:bg-black hover:text-white"
                            onClick={handleBack}
                          >
                            Exit
                          </Button>
                        </Link>

                        <div className="flex flex-col items-end w-full">
                          <Button
                            variant="secondary"
                            type="submit"
                            className="group w-full font-sans rounded-none bg-black text-white hover:bg-black/90"
                          >
                            Reserve my design
                            <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-3">
                              →
                            </span>
                          </Button>

                          <p className="text-xs font-sans text-black/50 mt-2 w-full text-center italic">
                            (No payment required to reserve)
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </Form>
              )}

              {/* --- Slide 20: Thank you / next steps --- */}
              {!isPlayground && step === thankYouSlide && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex gap-2 text-sm text-black font-sans items-center">
                      <CheckCircle className="h-4 w-4 text-black" strokeWidth={1.75} />
                      <span>Reserved</span>
                    </div>
                    <h1 className="text-3xl font-kalice font-medium text-black">
                      Thank you.
                    </h1>
                    <p className="text-sm font-sans text-black/60">
                      Your Labyrinth Scarf is reserved.
                      We’ll contact you shortly to introduce you to our beloved production steps – bringing your design to life.

                      If you’d rather set a time yourself, you can schedule an appointment below.
                    </p>
                  </div>



                  <div className="flex flex-col gap-2">
                    <BookingModal
                      trigger={
                        <Button
                          asChild
                          variant="secondary"
                          className="group tracking-wide w-full font-sans rounded-none bg-black text-white hover:bg-black/90"
                        >
                          <span>
                            Book a call
                            <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-3">
                              →
                            </span>
                          </span>
                        </Button>
                      }
                    />


                    <Link
                      to="/">
                      <Button
                        variant="outline"
                        className="w-full border-2 border-black rounded-none font-sans text-black hover:bg-black hover:text-white"
                        onClick={() => navigate("/")}
                      >
                        Return home
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

{/* --- Slide 21: Check out --- */}
{!isPlayground && step === checkoutSlide && (
  <div className="space-y-6">
    <div className="space-y-2">
      <div className="flex gap-2 text-sm text-black font-sans items-center">
        <CheckCircle className="h-4 w-4 text-black" strokeWidth={1.75} />
        <span>Completed</span>
      </div>
      <h1 className="text-3xl font-kalice font-medium text-black">
        Your design is complete.
      </h1>
      <p className="text-sm font-sans text-black/60">
        This piece has been created specifically for you.
        You can review it once more or continue to checkout when ready.
      </p>
    </div>

    <div className="flex justify-between space-x-2">
      <Button
        type="button"
        variant="outline"
        onClick={() => setStep(amountOfQuestions)}
        className=" px-12 rounded-none font-sans border-2 border-black text-black hover:bg-black hover:text-white bg-transparent flex items-center"
      >
        <SquarePen className='w-4 h-4 mr-2'/> Edit
      </Button>

      <Button
        type="button"
        className="snipcart-add-item flex items-center justify-center gap-2 rounded-none font-sans text-white bg-black hover:bg-[#111] hover:text-white w-full"
        data-item-id={storeProduct?.slug || "labyrinth-scarf"}
        data-item-name={storeProduct?.title || "Labyrinth Scarf"}
        data-item-price={checkoutPrice}
        data-item-url={`${window.location.origin}/api/snipcartProduct?slug=${storeProduct?.slug}`}
        data-item-description={confirmationDescription}
        data-item-image={absoluteImageUrl}
        data-item-quantity="1"
        data-item-metadata={JSON.stringify({
          kind: "personalised",
          baseSlug: storeProduct?.slug || "labyrinth-scarf",
          creationId,
          productVersionSlug: PRODUCT_VERSION_SLUG,
        })}
      >
        <ShoppingBag className="w-4 h-4" />
        Continue <span>→</span>
      </Button>
    </div>
  </div>
)}

              {/* --- Slide 22: PlaygroundSlide --- */}
              {isPlayground && step === playgroundSlide && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <div className="flex gap-2 text-sm text-black font-sans items-center">
                      <CheckCircle className="h-4 w-4 text-black" strokeWidth={1.75} />
                      <span>Completed</span>
                    </div>
                    <h1 className="text-3xl font-kalice font-medium text-black">
                      Your design is complete.
                    </h1>
                    <p className="text-sm font-sans text-black/60">
                      Let us know what you think!
                    </p>
                  </div>

                  <div className="">
                    <Link
                      to="/members/playground">
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 w-full rounded-none font-sans text-white bg-black hover:bg-[#111] hover:text-white"
                        onClick={() => navigate("/members/playground")}
                      >
                        <Shapes className='w-4 h-4' /> Return to playground
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

            </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  );
};

export default MyForm;
